import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addItemToLast, pushToVersions, setCategoryToEdit, setSectionFormType, updateVersionIndex } from '../../../../../Features/storefrontSlice';
import CrossGray from '../../../../../Assets/StorefrontIcons/CrossGray.svg';
import CategoryBox from './CategoryBox';
import { StorefrontContext } from '../../../../../Context/StorefrontContext';
import { getCategory } from '../../../../../lib/utils/apiCalls';
import { v4 as uuidv4 } from 'uuid';
import { apiV1 } from '../../../../../Auth/Interceptor';

function CategoryForm() {
    const dispatch = useDispatch();

    const categoryToEdit = useSelector((state) => state.storefront.categoryToEdit);
    const versionStack = useSelector((state) => state.storefront.versionStack);
    const versionIndex = useSelector((state) => state.storefront.versionIndex);

    const [categoryId, setCategoryId] = useState('');

    const defaultFormErrors = {
        title: '',
        categoryId: ''
    }
    
    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    const {
        categorySection,
        setCategorySection,
    } = useContext(StorefrontContext);

    const [categoryList, setCategoryList] = useState([]);

    function closeForm() {
        dispatch(setSectionFormType(null));
        setCategorySection({
            categories: [],
            title: '',
        });
    }

    function handleChange(e) {
        setCategorySection({
            ...categorySection,
            title: e.target.value,
        });
    }

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await apiV1.get('category/getAll');
                if(response?.status === 200) {
                    setCategoryList(response.data.data.category);
                }
                else {
                    // showToast('Error fetching categories.', 'error');
                }
            }
            catch(err) {
                // showToast('Error fetching categories.', 'error');
            }
        }

        fetchCategories();
    }, []);

    function handleSave() {
        if(categoryId) {
            const currentVersion = versionStack[versionIndex];

            const tempVersionData = [ ...currentVersion.versionData ];

            const newCategoryData = categorySection.categories.map(category => {
                return {
                    id: category.id,
                    imageLink: category.categoryImgUrl[0],
                    name: category.categoryName,
                }
            });

            const categoryDataIndex = tempVersionData.findIndex(
                item =>
                    item.item.typeData === 'CATEGORY' && 
                    item.item.id === categoryId 
            );

            if (categoryDataIndex !== -1) {
                tempVersionData[categoryDataIndex] = {
                    ...tempVersionData[categoryDataIndex],
                    item: {
                        ...tempVersionData[categoryDataIndex].item,
                        name: categorySection.title,
                        categoryLinkList: newCategoryData,
                    }
                };
            }

            const newIndex = versionStack[versionStack.length - 1].index + 1;

            const newVersion = {
                ...currentVersion,
                index: newIndex,
                versionData: tempVersionData,
                comment: 'update category',
            }

            dispatch(pushToVersions(newVersion));
            dispatch(updateVersionIndex(newIndex));
        }
        else {
            const validationErrors = {};

            if(!categorySection.title) {
                validationErrors.title = 'Required field';
            }
            if(categorySection.categories.length === 0) {
                validationErrors.categoryId = 'Select at least one category';
            }

            if(Object.keys(validationErrors).length === 0) {
                const list = categorySection.categories.map(category => {
                    return {
                        id: category.id,
                        imageLink: category.categoryImgUrl[0],
                        name: category.categoryName,
                    }
                });

                const object = {
                    typeData: 'CATEGORY',
                    name: categorySection.title,
                    id: uuidv4(),
                    categoryLinkList: list,
                }

                dispatch(addItemToLast(object));
            }
            else {
                setFormErrors(validationErrors);
            }
        }
        closeForm();
    }

    useEffect(() => {
        async function setCategory(id) {
            if(id) {
                const tempVersion = versionStack[versionIndex]?.versionData;

                if (tempVersion && tempVersion.length > 0) {
                    const foundItem = tempVersion.find(item => item.item.typeData === 'CATEGORY' && item.item.id === id);

                    if(foundItem) {
                        const { item } = foundItem;

                        try {
                            const categoryList = await Promise.all(item.categoryLinkList.map(async category => {
                                const response = await getCategory(category.id);
                                if (response?.status === 200) {
                                    return response.data.data.category;
                                }
                                return null;
                                })
                            );

                            const filteredCategories = categoryList.filter(category => category !== null);

                            setCategorySection({
                                categories: filteredCategories,
                                title: item.name,
                            });
                            setCategoryId(id);
                        }
                        catch (error) {
                            console.error('Error fetching categories:', error);
                        }
                    }
                }
            }
        }

        setCategory(categoryToEdit);
        dispatch(setCategoryToEdit(null));
        // eslint-disable-next-line
    }, [categoryToEdit]);

    return (
        <div className='w-[305px] h-full bg-[#FFFFFF] border-l border-[#EAEAEA] flex flex-col gap-4 select-none relative'>
            <div className="flex items-center justify-between">
                <p className='font-semibold text-[20px] leading-6 text-[#121212] pl-3'>Category</p>

                <div className="size-11 p-[10px] flex items-center justify-center">
                    <button onClick={closeForm} className='size-6 flex items-center justify-center outline-none'>
                        <img src={CrossGray} alt="" />
                    </button>
                </div>
            </div>

            <div className="h-[calc(100%-44px)] flex flex-col gap-3 px-3 overflow-y-scroll no-scrollbar pb-16">
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <label htmlFor="section-name" className='font-medium text-[14px] leading-[22px] text-[#121212]'>Section name</label>

                        <p className='text-[12px] leading-5 text-[#EF4444]'>{formErrors.title}</p>
                    </div>

                    <input 
                        id='section-name' 
                        name='section-name' 
                        className='outline-none border text-base border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-[9px] text-[#121212] placeholder:text-[#666666]' 
                        placeholder='Enter section name' 
                        type="text" 
                        value={categorySection.title} 
                        onChange={handleChange} 
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <p className='font-medium text-base text-[#000000]'>Categories</p>

                        <p className='text-[12px] leading-5 text-[#EF4444]'>{formErrors.categoryId}</p>
                    </div>

                    {categoryList?.map(category => {
                        const isSelected = categorySection.categories.find(item => item.id === category.id);

                        return (
                            <CategoryBox 
                                key={category.id} 
                                id={category.id} 
                                category={category} 
                                isSelected={isSelected} 
                            />
                        );
                    })}
                </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full p-3 bg-[#FFFFFF]">
                <button onClick={handleSave} className='w-full outline-none px-4 py-[6px] bg-primaryColor hover:bg-primaryAccent rounded-md font-semibold text-[12px] leading-6 text-buttonText'>Save changes</button>
            </div>
        </div>
    );
}

export default CategoryForm;
